import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Crown, Plus, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PremiumAdmin() {
  const [prices, setPrices] = useState({});
  const [newProduct, setNewProduct] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/admin/premium-pricing`, { withCredentials: true })
      .then(res => setPrices(res.data || {}))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    try {
      await axios.put(`${API}/api/admin/premium-pricing`, { prices }, { withCredentials: true });
      toast.success('Prix Premium mis à jour');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleAddProduct = () => {
    if (!newProduct.trim() || !newPrice) return;
    setPrices(prev => ({ ...prev, [newProduct.trim()]: parseFloat(newPrice) }));
    setNewProduct('');
    setNewPrice('');
  };

  const handleRemoveProduct = (key) => {
    setPrices(prev => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  };

  const LEVELS = [
    { name: 'BIJOU', discount: 10, color: '#e91e63' },
    { name: 'ARGENT', discount: 15, color: '#9e9e9e' },
    { name: 'PRIME', discount: 20, color: '#c3263b' },
    { name: 'OR', discount: 30, color: '#ffc107' },
  ];

  if (loading) return <Layout><div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" /></div></Layout>;

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Crown size={24} className="text-amber-600" />
          <h1 className="text-2xl font-semibold text-slate-900">Premium — Grille tarifaire</h1>
        </div>

        {/* Add product */}
        <Card className="border-slate-200">
          <CardHeader><CardTitle className="text-base">Ajouter un produit</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label className="text-xs">Nom du produit</Label>
                <Input value={newProduct} onChange={e => setNewProduct(e.target.value)} placeholder="Ex: Maillot, Kit (Maillot + Short)..." className="mt-1" />
              </div>
              <div className="w-48">
                <Label className="text-xs">Prix public (VND)</Label>
                <Input type="number" value={newPrice} onChange={e => setNewPrice(e.target.value)} placeholder="Ex: 500000" className="mt-1" />
              </div>
              <Button onClick={handleAddProduct} className="gap-1"><Plus size={14} /> Ajouter</Button>
            </div>
          </CardContent>
        </Card>

        {/* Price grid */}
        {Object.keys(prices).length > 0 && (
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Produits et prix par niveau</CardTitle>
                <Button onClick={handleSave} className="gap-1 bg-emerald-600 hover:bg-emerald-700"><Save size={14} /> Sauvegarder</Button>
              </div>
            </CardHeader>
            <CardContent>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 px-3 font-semibold">Produit</th>
                    <th className="text-right py-2 px-3 font-semibold">Prix public</th>
                    {LEVELS.map(l => (
                      <th key={l.name} className="text-right py-2 px-3 font-semibold" style={{ color: l.color }}>{l.name} (-{l.discount}%)</th>
                    ))}
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(prices).map(([product, price]) => (
                    <tr key={product} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2.5 px-3 font-medium">{product}</td>
                      <td className="py-2.5 px-3 text-right">
                        <Input type="number" value={price} onChange={e => setPrices(prev => ({ ...prev, [product]: parseFloat(e.target.value) || 0 }))} className="h-8 w-32 text-sm text-right ml-auto" />
                      </td>
                      {LEVELS.map(l => (
                        <td key={l.name} className="py-2.5 px-3 text-right font-medium" style={{ color: l.color }}>
                          {Math.round(Number(price) * (1 - l.discount / 100)).toLocaleString()} VND
                        </td>
                      ))}
                      <td className="py-2.5 px-1">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => handleRemoveProduct(product)}>
                          <Trash2 size={14} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* Level thresholds reference */}
        <Card className="border-slate-200">
          <CardHeader><CardTitle className="text-base">Seuils des niveaux (référence)</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              {LEVELS.map((l, i) => (
                <div key={l.name} className="text-center p-4 rounded-lg border-2" style={{ borderColor: l.color }}>
                  <p className="font-bold text-sm" style={{ color: l.color }}>{l.name}</p>
                  <p className="text-xl font-black text-slate-900">{[20, 60, 100, 300][i]}M VND</p>
                  <p className="text-xs text-slate-500">par an</p>
                  <p className="text-sm font-semibold mt-1" style={{ color: l.color }}>-{l.discount}%</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
